// ============================================================
// COMBAT RULES - derives the human-readable "Combat Rules" card
// entries from a target's flat trait flags. Pure function, used
// by BOTH Overview sheets (the attacker derives its input via
// characterToTarget). Extracted from TargetCharacteristicsTab
// (v6.32.1).
// ============================================================
import { SIZE_MODS } from './constants';
import type { Target } from '../types';

export interface CombatRule {
  name: string;
  description: string;
}

/** The subset of Target fields the rules read (any Target satisfies it). */
export type CombatRulesInput = Pick<
  Target,
  | 'size'
  | 'stuffOfNightmares'
  | 'incorporeal'
  | 'fromBeyond'
  | 'astartes'
  | 'trueGrit'
  | 'xenos'
  | 'regeneration'
  | 'naturalArmour'
  | 'machineAP'
  | 'daemonicProtection'
  | 'fearRating'
  | 'horde'
>;

type RuleRow = (t: CombatRulesInput) => CombatRule | null;

const rule = (name: string, description: string): CombatRule => ({ name, description });
const positive = (n: number | undefined) => (n || 0) > 0;

function sizeRule(t: CombatRulesInput): CombatRule | null {
  if (!t.size || t.size === 'Average') return null;
  const mod = SIZE_MODS[t.size];
  const sign = mod >= 0 ? '+' : '';
  return rule(`Size: ${t.size}`, `${sign}${mod} to hit this target`);
}

/** The card's rows, in display order; each returns its entry or null when the trait is absent. */
const RULE_ROWS: readonly RuleRow[] = [
  sizeRule,
  (t) => (t.stuffOfNightmares ? rule('Stuff of Nightmares', 'Immune to Stunning/Bleeding. Special death rules') : null),
  // Must match damageCalc's actual rule: everything is BLOCKED except
  // Force, Warp or Sanctified weapons - the old "half damage" prose
  // described a rule this engine never implemented (v8.271.1).
  (t) => (t.incorporeal ? rule('Incorporeal', 'Only damaged by Force, Warp or Sanctified weapons') : null),
  (t) => (t.fromBeyond ? rule('From Beyond', 'Immune to Fear, Pinning and delusion') : null),
  (t) =>
    t.astartes
      ? rule(
          'Astartes',
          'Blood Loss immune (Warp Weapons bypass) · Toughness re-roll vs Toxic · grants the full talent package',
        )
      : null,
  // True Grit is its own switch (v6.34.4) - shown whenever actually active,
  // so a GM who disables it on an Astartes target sees the rule disappear.
  (t) => (t.trueGrit ? rule('True Grit', 'Critical severity halved (round up, min 1)') : null),
  (t) =>
    t.xenos ? rule('Xenos', 'Attackers with Deathwatch Training auto-confirm Righteous Fury vs this target') : null,
  (t) => (positive(t.regeneration) ? rule('Regeneration', `Recovers ${t.regeneration} wound(s)/turn`) : null),
  (t) => (positive(t.naturalArmour) ? rule('Natural Armour', `+${t.naturalArmour} AP all locations`) : null),
  (t) => (positive(t.machineAP) ? rule('Machine', `+${t.machineAP} AP all locations`) : null),
  (t) =>
    positive(t.daemonicProtection) ? rule('Daemonic', `+${t.daemonicProtection} effective Toughness Bonus`) : null,
  (t) => (positive(t.fearRating) ? rule(`Fear (${t.fearRating})`, 'Enemies must pass WP test') : null),
  (t) => (t.horde?.isHorde ? rule('Horde', `Magnitude ${t.horde.magnitude}/${t.horde.maxMagnitude}`) : null),
];

export function deriveCombatRules(target: CombatRulesInput): CombatRule[] {
  return RULE_ROWS.map((row) => row(target)).filter((r): r is CombatRule => r !== null);
}
